"use client";

import React, { useCallback } from "react";
import { cn } from "utils";

export interface NumericInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  precision?: number; // 小数位数，默认无限
  maxIntegerDigits?: number; // 整数部分的最大位数，默认无限
  prefix?: string; // 前缀符号
  className?: string;
}

export const NumericInput: React.FC<NumericInputProps> = ({
  value,
  onChange,
  placeholder = "0.00",
  precision,
  maxIntegerDigits,
  prefix,
  className,
}) => {
  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      let newValue = event.target.value;

      // 允许空值
      if (newValue === "") {
        onChange("");
        return;
      }

      // 移除所有负号
      newValue = newValue.replace(/-/g, "");

      // 构建允许的正则表达式（只允许数字和小数点）
      const regex = /^\d*\.?\d*$/;

      // 验证输入格式
      if (!regex.test(newValue)) {
        return; // 如果格式不正确，不更新
      }

      // 精度控制
      if (precision !== undefined && precision >= 0) {
        const parts = newValue.split(".");
        if (parts.length > 1 && parts[1].length > precision) {
          newValue = `${parts[0]}.${parts[1].substring(0, precision)}`;
        }
      }

      // 整数位数控制
      if (maxIntegerDigits !== undefined && maxIntegerDigits >= 0) {
        const parts = newValue.split(".");
        if (parts[0].length > maxIntegerDigits) {
          // 截断整数部分
          parts[0] = parts[0].substring(0, maxIntegerDigits);
          newValue = parts.length > 1 ? `${parts[0]}.${parts[1]}` : parts[0];
        }
      }

      // 防止多个小数点
      const decimalPoints = (newValue.match(/\./g) || []).length;
      if (decimalPoints > 1) {
        const parts = newValue.split(".");
        newValue = `${parts[0]}.${parts.slice(1).join("")}`;
      }

      // 移除整数部分的前导零，但保留小数点后的零
      newValue = newValue.replace(
        /^(\d+)(\.\d*)?$/,
        (_, integerPart, decimalPart) => {
          // 如果整数部分为 "0" 或为空，保持不变
          if (integerPart === "0" || integerPart === "") {
            return integerPart + (decimalPart || "");
          }
          // 移除前导零
          const cleanedInteger = integerPart.replace(/^0+/, "");
          return (cleanedInteger || "0") + (decimalPart || "");
        },
      );

      onChange(newValue);
    },
    [precision, maxIntegerDigits, onChange],
  );

  return (
    <div
      className={cn(
        "relative flex h-9 w-full items-center gap-2 rounded-2 border border-solid border-border_white_transparent10 bg-background_white_overlay5 px-3 body_m text-content_secondary transition-colors",
        "focus-within:outline-none focus-within:border-content_brand_primary/50",
        className,
      )}
    >
      {prefix && (
        <span className="text-content_secondary whitespace-nowrap">
          {prefix}
        </span>
      )}
      <input
        type="text"
        value={value}
        onChange={handleInputChange}
        placeholder={placeholder}
        className="h-full w-full bg-transparent focus:outline-none placeholder:text-content_quaternary text-content_secondary"
      />
    </div>
  );
};
